import { Icon } from '@/components/Icon'
import React, { useRef } from 'react'
import FileCard from './FileCard'

// 文件状态类型
export type FileStatus = 'WAITING' | 'UPLOADING' | 'SUCCESS' | 'ERROR'

// 上传文件接口
export interface UploadFile {
  id: string
  file: File
  progress: number // 0-100
  status: FileStatus
  error?: string
  serverId?: string // 服务器返回的文件ID
}

// 文件上传组件配置
export interface UploadFileConfig {
  /** 支持的文件类型 */
  acceptedTypes?: string[]
  /** 最大文件数量 */
  maxFiles?: number
  /** 最大文件大小 (MB) */
  maxSizeMB?: number
  /** 最大PDF页数 */
  maxPdfPages?: number
  /** 是否支持多文件选择 */
  multiple?: boolean
  /** 自定义描述文本 */
  description?: React.ReactNode
  /** 自定义提示文本 */
  tips?: React.ReactNode
  /** 上传区域图标 */
  icon?: React.ReactNode
  /** 上传区域图标大小 */
  iconSize?: number
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义类名 */
  className?: string
}

// 文件上传组件属性
export interface UploadFileProps extends UploadFileConfig {
  /** 文件列表 */
  files?: UploadFile[]
  /** 文件列表变化回调 */
  onFilesChange?: (files: UploadFile[]) => void
  /** 文件选择回调 */
  onFileSelect?: (files: File[]) => void
  /** 文件删除回调 */
  onFileRemove?: (fileId: string) => void
  /** 是否显示文件列表 */
  showFileList?: boolean
  /** 文件列表类名 */
  fileListClassName?: string
}

// 默认配置
const DEFAULT_CONFIG: Required<
  Pick<
    UploadFileConfig,
    'acceptedTypes' | 'maxFiles' | 'maxSizeMB' | 'multiple' | 'iconSize'
  >
> = {
  acceptedTypes: ['.pdf', '.txt', '.doc', '.docx', '.md'],
  maxFiles: 300,
  maxSizeMB: 100,
  multiple: true,
  iconSize: 108,
}

const UploadFile: React.FC<UploadFileProps> = ({
  // 配置项
  acceptedTypes = DEFAULT_CONFIG.acceptedTypes,
  maxFiles = DEFAULT_CONFIG.maxFiles,
  maxSizeMB = DEFAULT_CONFIG.maxSizeMB,
  maxPdfPages,
  multiple = DEFAULT_CONFIG.multiple,
  description,
  tips,
  icon,
  iconSize = DEFAULT_CONFIG.iconSize,
  disabled = false,
  className,

  // 回调函数
  files = [],
  onFilesChange,
  onFileSelect,
  onFileRemove,

  // 显示控制
  showFileList = true,
  fileListClassName,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 默认描述文本
  const defaultDescription = (
    <>
      <div className="text-sm text-gray-400 mb-1">
        支持 {acceptedTypes.join('、')} 格式，最多 {maxFiles}{' '}
        个文件，每个文件不超过 {maxSizeMB}MB
      </div>
      {maxPdfPages && (
        <div className="text-sm text-gray-400">
          PDF 文件最多 {maxPdfPages} 页
        </div>
      )}
    </>
  )

  // 默认图标
  const defaultIcon = <Icon name="uploadCloud" size={iconSize} />

  // 选择文件
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (!selectedFiles) return

    const newFiles: UploadFile[] = []
    const validFiles: File[] = []

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i]

      // 校验文件类型
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!ext || !acceptedTypes.includes('.' + ext)) {
        console.warn(`不支持的文件类型: ${file.name}`)
        continue
      }

      // 校验文件大小
      if (file.size > maxSizeMB * 1024 * 1024) {
        console.warn(`文件过大: ${file.name}`)
        continue
      }

      // 这里可以添加PDF页数校验逻辑
      // if (ext === 'pdf' && maxPdfPages) {
      //   // PDF页数校验逻辑
      // }

      const uploadFile: UploadFile = {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        progress: 0,
        status: 'WAITING',
      }

      newFiles.push(uploadFile)
      validFiles.push(file)
    }

    // 限制文件数量
    const updatedFiles = [...files, ...newFiles].slice(0, maxFiles)

    // 触发回调
    onFilesChange?.(updatedFiles)
    onFileSelect?.(validFiles)

    // 清空input
    e.target.value = ''
  }

  // 删除文件
  const handleRemove = (id: string) => {
    const updatedFiles = files.filter((f) => f.id !== id)
    onFilesChange?.(updatedFiles)
    onFileRemove?.(id)
  }

  // 点击上传区域
  const handleUploadAreaClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  // 渲染文件卡片
  const renderFileCard = (file: UploadFile) => {
    return (
      <FileCard
        key={file.id}
        file={{
          id: file.id,
          name: file.file.name,
          size: file.file.size,
          file: file.file,
          progress: file.progress,
          status: file.status,
          error: file.error,
        }}
        type="upload"
        showDelete={true}
        onDelete={handleRemove}
      />
    )
  }

  // 渲染文件列表
  const renderFileList = () => {
    if (!showFileList || files.length === 0) return null

    return <div className={fileListClassName}>{files.map(renderFileCard)}</div>
  }

  return (
    <div className={className}>
      {/* 上传区域 */}
      <div
        className={`
          flex flex-col items-center justify-center 
          border-[1px] border-dashed border-[#4E57FE1A] 
          rounded-lg bg-[#4E57FE05] py-12 mb-8 
          transition-colors
          ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer hover:border-[#8047FF]'
          }
        `}
        onClick={handleUploadAreaClick}
      >
        {icon || defaultIcon}
        {description || defaultDescription}
        {tips && <div className="text-sm text-gray-400 mt-2">{tips}</div>}

        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedTypes.join(',')}
          className="hidden"
          onChange={handleFileChange}
          disabled={disabled}
        />
      </div>

      {/* 文件列表 */}
      {renderFileList()}
    </div>
  )
}

export default UploadFile
